/**
 * "Can't do. Why?" (Tasks Both Ways slice 3, 10/28, pick 4A —
 * https://claude.ai/artifact/H7gsi8ERjULUpGCpUMZzH8).
 *
 * Four one-tap reasons and "Say it myself". One tap sends the task back to
 * the person who asked, in red, with the reason. Nothing pings anyone's
 * phone. The same dialog is used on the computer (Mine, the task drawer) and
 * on the iPad, so `big` makes every button iPad-sized.
 */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Ban, KeyRound, MessageSquare, Package, UserRoundX, Users } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { firstName } from "@/lib/tasks";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

/** Same keys and words as the server (services/taskCantDo.ts). */
export const CANT_DO_REASONS: Array<{ key: string; words: string; icon: typeof Ban }> = [
    { key: "locked_out", words: "Locked out", icon: KeyRound },
    { key: "customer_no", words: "Customer said no", icon: UserRoundX },
    { key: "need_part", words: "Need a part", icon: Package },
    { key: "need_someone", words: "Needs someone else", icon: Users },
];

export function CantDoDialog({
    open,
    taskId,
    taskTitle,
    askerName,
    big = false,
    onDone,
    onClose,
}: {
    open: boolean;
    taskId: string | null;
    taskTitle?: string | null;
    askerName?: string | null;
    /** iPad: bigger buttons and text. */
    big?: boolean;
    onDone: () => void;
    onClose: () => void;
}) {
    const [own, setOwn] = useState(false);
    const [note, setNote] = useState("");
    useEffect(() => {
        if (open) {
            setOwn(false);
            setNote("");
        }
    }, [open]);
    const who = askerName ? firstName(askerName) : "the person who asked";
    const mut = useMutation({
        mutationFn: (body: { reasonKey: string; note?: string }) => api.post(`/api/tasks/${taskId}/cant-do`, body),
        onSuccess: () => {
            toast.success(`Sent back to ${who} in red, with your reason.`);
            onDone();
            onClose();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't save. Try again."),
    });
    return (
        <Dialog open={open} onClose={onClose} title="Can't do. Why?">
            <div className="space-y-3" data-testid="cant-do-dialog">
                {taskTitle && <p className={cn("font-medium", big ? "text-lg" : "text-sm")}>{taskTitle}</p>}
                <div className="grid grid-cols-2 gap-2">
                    {CANT_DO_REASONS.map((r) => (
                        <button
                            key={r.key}
                            type="button"
                            disabled={mut.isPending || !taskId}
                            onClick={() => mut.mutate({ reasonKey: r.key })}
                            data-testid={`cant-do-${r.key}`}
                            className={cn(
                                "flex items-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 text-left font-semibold text-red-900 hover:border-red-500 disabled:opacity-60 dark:border-red-900 dark:bg-red-950/50 dark:text-red-100",
                                big ? "min-h-16 px-4 text-lg" : "min-h-12 px-3 text-sm",
                            )}
                        >
                            <r.icon className={cn("shrink-0", big ? "h-6 w-6" : "h-4 w-4")} />
                            {r.words}
                        </button>
                    ))}
                </div>
                {own ? (
                    <div className="space-y-2">
                        <Textarea
                            rows={2}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={`Say why in a few words. ${who === "the person who asked" ? "They" : who} sees this.`}
                            maxLength={1000}
                            autoFocus
                            className={big ? "text-lg" : undefined}
                        />
                        <Button
                            className="w-full bg-red-700 text-white hover:bg-red-800"
                            size={big ? "lg" : "default"}
                            disabled={!note.trim() || mut.isPending || !taskId}
                            onClick={() => mut.mutate({ reasonKey: "own", note: note.trim() })}
                        >
                            <Ban className="h-4 w-4" /> Send it back
                        </Button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setOwn(true)}
                        className={cn(
                            "flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border font-semibold text-foreground hover:bg-muted",
                            big ? "min-h-14 text-lg" : "min-h-11 text-sm",
                        )}
                    >
                        <MessageSquare className={big ? "h-5 w-5" : "h-4 w-4"} /> Say it myself
                    </button>
                )}
                <p className={cn("text-muted-foreground", big ? "text-base" : "text-xs")}>
                    One tap. It goes back to {who} in red with your answer. Nothing pings anyone&apos;s phone.
                </p>
            </div>
        </Dialog>
    );
}

/**
 * The asker's three answers to a can't-do: I'll do it, Give to…, Drop it.
 * Shared by the Came back card and the task drawer.
 */
export function useCantDoDecide(taskId: string, onChanged: () => void) {
    return useMutation({
        mutationFn: (body: { decision: "mine" | "give" | "drop"; userId?: string }) =>
            api.post(`/api/tasks/${taskId}/cant-do/decide`, body),
        onSuccess: (_d, body) => {
            toast.success(
                body.decision === "mine"
                    ? "It's yours now. It's on your Mine list."
                    : body.decision === "give"
                      ? "Given. They start fresh."
                      : "Dropped. It's closed.",
            );
            onChanged();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't save. Try again."),
    });
}
